import { useFormContext } from 'react-hook-form';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import {
  integrationRewriteSectionActiveCount,
  integrationRewriteSections,
} from '../../../integrationRewriteModel';
import { IntegrationRewriteSection } from '../../../integrationRewriteModel';
import type { IntegrationFormValue } from '../../../integrationFormSchema';
import { IntegrationRewriteRow } from './IntegrationRewriteRow';

export function IntegrationInlineRewrite() {
  const form = useFormContext<IntegrationFormValue>();
  const sets = form.watch('integrationRewriteSets');

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">
          {dashboardT('integrations.rewrite.inlineTitle')}
        </h3>
        <p className="text-muted-foreground mt-1 text-sm">
          {dashboardT('integrations.rewrite.inlineDescription')}
        </p>
      </div>
      <Accordion type="multiple" className="border-input overflow-hidden rounded-md border">
        {integrationRewriteSections.map((section) => {
          const activeCount = integrationRewriteSectionActiveCount(section, sets);
          return (
            <AccordionItem
              key={section.id}
              value={section.id}
              className="border-input border-b last:border-b-0"
            >
              <AccordionTrigger className="bg-muted/20 px-4 py-4 hover:no-underline">
                <span className="flex min-w-0 flex-1 items-center justify-between gap-4 pr-3">
                  <span className="font-semibold">{rewriteSectionLabel(section.id)}</span>
                  <span className="text-muted-foreground text-xs font-medium">
                    {activeCount
                      ? dashboardT(
                          activeCount === 1
                            ? 'integrations.rewrite.activeProperty'
                            : 'integrations.rewrite.activeProperties',
                          { count: activeCount },
                        )
                      : dashboardT('integrations.rewrite.inactive')}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-0">
                <div className="bg-muted/10 border-input hidden grid-cols-[minmax(150px,0.8fr)_minmax(0,1.5fr)_90px_90px] gap-3 border-t px-4 py-3 text-xs font-semibold md:grid">
                  <span>{dashboardT('integrations.rewrite.property')}</span>
                  <span>{dashboardT('integrations.rewrite.value')}</span>
                  <span className="text-center">{dashboardT('integrations.rewrite.rewrite')}</span>
                  <span className="text-center">{dashboardT('integrations.rewrite.nullify')}</span>
                </div>
                {section.fields.map((fieldName) => {
                  const index = sets.findIndex((set) => set.field === fieldName);
                  return index >= 0 ? (
                    <IntegrationRewriteRow key={fieldName} fieldName={fieldName} index={index} />
                  ) : null;
                })}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
}

function rewriteSectionLabel(section: IntegrationRewriteSection) {
  const labels = {
    [IntegrationRewriteSection.App]: 'integrations.rewrite.sections.app',
    [IntegrationRewriteSection.Site]: 'integrations.rewrite.sections.site',
    [IntegrationRewriteSection.User]: 'integrations.rewrite.sections.user',
    [IntegrationRewriteSection.Video]: 'integrations.rewrite.sections.video',
    [IntegrationRewriteSection.Device]: 'integrations.rewrite.sections.device',
    [IntegrationRewriteSection.Content]: 'integrations.rewrite.sections.content',
    [IntegrationRewriteSection.Producer]: 'integrations.rewrite.sections.producer',
    [IntegrationRewriteSection.Network]: 'integrations.rewrite.sections.network',
    [IntegrationRewriteSection.Channel]: 'integrations.rewrite.sections.channel',
    [IntegrationRewriteSection.Regulations]: 'integrations.rewrite.sections.regulations',
    [IntegrationRewriteSection.Impression]: 'integrations.rewrite.sections.impression',
    [IntegrationRewriteSection.Publisher]: 'integrations.rewrite.sections.publisher',
    [IntegrationRewriteSection.Source]: 'integrations.rewrite.sections.source',
  } as const;
  return dashboardT(labels[section]);
}
